import React, { useEffect, useMemo, useState } from 'react';
import { MenuBookRounded, PublicRounded } from '@mui/icons-material';
import { getGradeChapters } from '../../services/api';
import { GradeSubjectChapters } from '../../types';

interface SyllabusTopicPickerProps {
  /** Fired with a chapter title when the student clicks a chapter chip.
   *  The chat sends it immediately as the first message. */
  onTopicSelect?: (topic: string) => void;
}

type Source = 'ncert' | 'global';

/**
 * Curriculum → subject → chapter picker on the empty chat screen.
 *
 * Step 1 (only when the class has BOTH curricula): pick NCERT or Global.
 * Step 2: pick a subject (Maths / Science / … or the Global subjects).
 * Step 3: pick a chapter — clicking it sends the title as the first message.
 *
 * Renders nothing if the student's grade has no chapters indexed or the fetch
 * fails, so the empty-state heading alone remains.
 */
const SyllabusTopicPicker: React.FC<SyllabusTopicPickerProps> = ({ onTopicSelect }) => {
  const [subjects, setSubjects] = useState<GradeSubjectChapters[]>([]);
  const [selectedSource, setSelectedSource] = useState<Source | null>(null);
  const [selectedSubject, setSelectedSubject] = useState<GradeSubjectChapters | null>(null);

  useEffect(() => {
    let cancelled = false;
    getGradeChapters()
      .then((res) => {
        if (!cancelled) setSubjects(res.subjects || []);
      })
      .catch(() => {
        // Graceful: leave subjects empty so the picker hides itself.
        if (!cancelled) setSubjects([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Split by curriculum (legacy rows with no source = NCERT).
  const bySource = useMemo(() => {
    const ncert = subjects.filter((s) => (s.source ?? 'ncert') === 'ncert');
    const global = subjects.filter((s) => s.source === 'global');
    return { ncert, global };
  }, [subjects]);

  const hasNcert = bySource.ncert.length > 0;
  const hasGlobal = bySource.global.length > 0;
  const needsCurriculumStep = hasNcert && hasGlobal;

  // When only one curriculum exists, skip the curriculum step entirely.
  const activeSource: Source | null =
    selectedSource ?? (needsCurriculumStep ? null : hasGlobal ? 'global' : 'ncert');

  if (subjects.length === 0) return null;

  const activeSubjects = activeSource ? bySource[activeSource] : [];

  const CURRICULA: { source: Source; label: string; hint: string; Icon: typeof MenuBookRounded }[] = [
    { source: 'ncert', label: 'NCERT', hint: 'Your standard syllabus', Icon: MenuBookRounded },
    { source: 'global', label: 'STEM', hint: 'Hands-on STEM modules', Icon: PublicRounded },
  ];

  // ── Step 1: curriculum tiles ──────────────────────────────────────────────
  if (!selectedSubject && needsCurriculumStep && !activeSource) {
    return (
      <div className="mt-6">
        <p className="mb-3 text-center text-sm font-medium text-slate-600">
          Or pick a topic from your syllabus
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          {CURRICULA.map(({ source, label, hint, Icon }) => (
            <button
              key={source}
              type="button"
              onClick={() => setSelectedSource(source)}
              className="flex w-64 max-w-full items-center gap-3 rounded-2xl border border-violet-200 bg-white px-4 py-3 text-left transition-colors hover:border-violet-400 hover:bg-violet-50"
            >
              <span className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-violet-100 text-violet-700">
                <Icon sx={{ fontSize: 22 }} />
              </span>
              <span>
                <span className="block text-sm font-semibold text-slate-800">{label}</span>
                <span className="block text-xs text-slate-500">{hint}</span>
              </span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ── Step 3: chapters ──────────────────────────────────────────────────────
  if (selectedSubject) {
    return (
      <div className="mt-6">
        <div className="mb-3 flex items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => setSelectedSubject(null)}
            className="text-sm font-medium text-violet-700 hover:text-violet-900"
          >
            ← Subjects
          </button>
          <span className="text-sm font-medium text-slate-600">{selectedSubject.label}</span>
        </div>
        <div className="flex flex-wrap justify-center gap-2">
          {selectedSubject.chapters.map((title) => {
            const prog = selectedSubject.chapter_progress?.find((c) => c.title === title);
            const youFinished = !!prog?.student_done;
            return (
              <button
                key={title}
                type="button"
                onClick={() => onTopicSelect?.(title)}
                className={`rounded-full border px-4 py-2 text-sm transition-colors ${
                  youFinished
                    ? 'border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100'
                    : 'border-violet-200 bg-white text-slate-700 hover:border-violet-300 hover:bg-violet-50'
                }`}
              >
                {title}
                {youFinished && (
                  <span className="ml-2 text-xs font-bold text-emerald-600">✓ You finished</span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // ── Step 2: subjects (within the active curriculum) ───────────────────────
  return (
    <div className="mt-6">
      {needsCurriculumStep ? (
        <div className="mb-3 flex items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => setSelectedSource(null)}
            className="text-sm font-medium text-violet-700 hover:text-violet-900"
          >
            ← Curriculum
          </button>
          <span className="text-sm font-medium text-slate-600">
            {activeSource === 'global' ? 'STEM' : 'NCERT'}
          </span>
        </div>
      ) : (
        <p className="mb-3 text-center text-sm font-medium text-slate-600">
          Or pick a topic from your syllabus
        </p>
      )}
      <div className="flex flex-wrap justify-center gap-2">
        {activeSubjects.map((s) => {
          // Show THIS student's own progress — chapters they've finished with
          // the coach out of the subject's total chapters (not the class ticks).
          const finished = s.student_completed ?? 0;
          const totalChapters = s.chapters.length;
          const hasChapters = totalChapters > 0;
          const pct = hasChapters ? Math.min(100, Math.round((finished / totalChapters) * 100)) : 0;
          return (
            <button
              key={`${s.source ?? 'ncert'}:${s.subject}`}
              type="button"
              onClick={() => setSelectedSubject(s)}
              className="rounded-2xl border border-violet-200 bg-violet-100 px-5 py-2 text-sm font-medium text-violet-700 transition-colors hover:bg-violet-200"
            >
              <span className="flex items-center gap-2">
                {s.label}
                {hasChapters && (
                  <span className="text-xs font-semibold text-emerald-600">
                    {finished}/{totalChapters}
                  </span>
                )}
              </span>
              {hasChapters && (
                <span className="mt-1.5 block h-1 w-full overflow-hidden rounded-full bg-emerald-100">
                  <span
                    className="block h-full rounded-full bg-emerald-500"
                    style={{ width: `${pct}%` }}
                  />
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default SyllabusTopicPicker;
